import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative md:min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Glowing Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full backdrop-blur-sm">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              <span className="text-cyan-400 text-sm font-medium">{t('hero.badge')}</span>
            </div>

            <div>
              <h2 className="text-6xl lg:text-7xl font-black text-white leading-tight mb-6">
                {t('hero.title')}
              </h2>
              <h3 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-500 bg-clip-text text-transparent">
                {t('hero.subtitle')}
              </h3>
            </div>

            <div className="flex flex-wrap gap-8 max-w-xl">
              <span className="text-xl text-gray-300 font-semibold tracking-widest">{t('hero.value1')}</span>
              <span className="text-gray-600 text-xl">|</span>
              <span className="text-xl text-gray-300 font-semibold tracking-widest">{t('hero.value2')}</span>
              <span className="text-gray-600 text-xl">|</span>
              <span className="text-xl text-gray-300 font-semibold tracking-widest">{t('hero.value3')}</span>
            </div>

            <div className="flex flex-wrap gap-4">
              <button className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 flex items-center gap-3 whitespace-nowrap cursor-pointer">
                {t('hero.ctaPrimary')}
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </button>
              <button className="px-8 py-4 bg-white/5 border border-white/10 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm">
                {t('hero.ctaSecondary')}
              </button>
            </div>
          </div>

          {/* Right Side - Tech Visualization */}
          <div className="relative hidden md:block">
            {/* Main Visual Container */}
            <div className="relative w-full aspect-square max-w-lg mx-auto">
              {/* Central Glow */}
              <div className="absolute inset-0 hidden md:flex items-center justify-center">
                <div className="w-64 h-64 bg-gradient-to-br from-cyan-500/30 to-teal-500/30 rounded-full blur-3xl animate-pulse"></div>
              </div>

              {/* Floating Code Blocks */}
              <div className="absolute inset-0 hidden md:block">
                <div className="absolute top-[10%] left-[15%] w-32 h-24 bg-slate-900/80 border border-cyan-500/30 rounded-lg backdrop-blur-sm p-3 animate-float" style={{ animationDelay: '0s' }}>
                  <div className="space-y-1.5">
                    <div className="h-1.5 bg-cyan-500/60 rounded w-3/4"></div>
                    <div className="h-1.5 bg-teal-500/40 rounded w-full"></div>
                    <div className="h-1.5 bg-cyan-500/40 rounded w-2/3"></div>
                  </div>
                </div>

                <div className="absolute top-[20%] right-[10%] w-28 h-20 bg-slate-900/80 border border-teal-500/30 rounded-lg backdrop-blur-sm p-3 animate-float" style={{ animationDelay: '1s' }}>
                  <div className="space-y-1.5">
                    <div className="h-1.5 bg-teal-500/60 rounded w-full"></div>
                    <div className="h-1.5 bg-cyan-500/40 rounded w-1/2"></div>
                  </div>
                </div>

                <div className="absolute bottom-[25%] left-[10%] w-36 h-28 bg-slate-900/80 border border-cyan-500/30 rounded-lg backdrop-blur-sm p-3 animate-float" style={{ animationDelay: '2s' }}>
                  <div className="space-y-1.5">
                    <div className="h-1.5 bg-cyan-500/60 rounded w-2/3"></div>
                    <div className="h-1.5 bg-teal-500/40 rounded w-full"></div>
                    <div className="h-1.5 bg-cyan-500/40 rounded w-3/4"></div>
                    <div className="h-1.5 bg-teal-500/30 rounded w-1/2"></div>
                  </div>
                </div>

                <div className="absolute bottom-[15%] right-[15%] w-32 h-24 bg-slate-900/80 border border-teal-500/30 rounded-lg backdrop-blur-sm p-3 animate-float" style={{ animationDelay: '1.5s' }}>
                  <div className="space-y-1.5">
                    <div className="h-1.5 bg-teal-500/60 rounded w-full"></div>
                    <div className="h-1.5 bg-cyan-500/40 rounded w-2/3"></div>
                    <div className="h-1.5 bg-teal-500/40 rounded w-4/5"></div>
                  </div>
                </div>
              </div>

              {/* Tech Icons Grid */}
              <div className="absolute inset-0 hidden md:flex items-center justify-center">
                <div className="grid grid-cols-3 gap-6">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-code-s-slash-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-cloud-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-database-2-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-brain-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-rocket-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-shield-check-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-smartphone-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-global-line text-2xl text-cyan-400"></i>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/40 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                    <i className="ri-settings-3-line text-2xl text-cyan-400"></i>
                  </div>
                </div>
              </div>

              {/* Connection Lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
                <line x1="50%" y1="50%" x2="30%" y2="30%" stroke="url(#gradient1)" strokeWidth="1" className="animate-pulse" />
                <line x1="50%" y1="50%" x2="70%" y2="30%" stroke="url(#gradient1)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
                <line x1="50%" y1="50%" x2="30%" y2="70%" stroke="url(#gradient1)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '1s' }} />
                <line x1="50%" y1="50%" x2="70%" y2="70%" stroke="url(#gradient1)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#14b8a6" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-1 h-1 bg-teal-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-3/4 left-2/3 w-2 h-2 bg-cyan-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-teal-300 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>
    </section>
  );
}
